/**
 * RFC-1612: which nodes are currently preparing a C2PA-signed download.
 *
 * Downloads start from several places (node toolbar, variants panel, the
 * "Download all clips" modal), but the loading indicator always belongs on the
 * node that owns the video. This module-level store is the single place both
 * sides meet: callers wrap signing in `beginC2paDownload(nodeId)`, and nodes
 * read `useIsPreparingC2paDownload(nodeId)`.
 *
 * It also de-duplicates in-flight signing per asset path, so repeat clicks (or
 * a node click racing a bulk download) never mint a second single-use signing
 * capability for the same file.
 */

import { useCallback, useSyncExternalStore } from 'react'

const preparingByNode = new Map<string, number>()
const inFlightAssets = new Map<string, Promise<string>>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function isPreparingC2paDownload(nodeId: string): boolean {
  return (preparingByNode.get(nodeId) ?? 0) > 0
}

/** Mark `nodeId` as preparing; call the returned function exactly once when done. */
export function beginC2paDownload(nodeId: string | undefined): () => void {
  if (!nodeId) return () => {}
  preparingByNode.set(nodeId, (preparingByNode.get(nodeId) ?? 0) + 1)
  emit()
  let ended = false
  return () => {
    if (ended) return
    ended = true
    const next = (preparingByNode.get(nodeId) ?? 1) - 1
    if (next > 0) preparingByNode.set(nodeId, next)
    else preparingByNode.delete(nodeId)
    emit()
  }
}

/**
 * Run `resolve` for `assetPath` once at a time. A second caller for the same
 * asset while the first is still signing gets `null` and should do nothing:
 * the first caller's download is already on its way.
 */
export async function runC2paDownloadOnce(
  assetPath: string,
  nodeId: string | undefined,
  resolve: () => Promise<string>,
): Promise<string | null> {
  if (inFlightAssets.has(assetPath)) return null
  const end = beginC2paDownload(nodeId)
  const pending = resolve()
  inFlightAssets.set(assetPath, pending)
  try {
    return await pending
  } finally {
    inFlightAssets.delete(assetPath)
    end()
  }
}

export function useIsPreparingC2paDownload(nodeId: string): boolean {
  const getSnapshot = useCallback(() => isPreparingC2paDownload(nodeId), [nodeId])
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Test-only reset; the store is module-global. */
export function resetC2paDownloadProgressForTests(): void {
  preparingByNode.clear()
  inFlightAssets.clear()
  emit()
}
